import { merchantName } from '../features/agency/merchantName';
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Panel,
  Notice,
  Pager,
  button,
  primary,
  input,
  useCan,
  useFilters,
  useResource,
  errorText,
} from "../features/agency/ui";
import * as api from "../features/advertising/service";
import {
  PLACEMENTS,
  STATUS,
  chinaDisplay,
  deliveryState,
  type Ad,
  type Action,
  type Payload,
  type Slot,
} from "../features/advertising/model";
import { AdEditor } from "../features/advertising/AdEditor";
import { CreativePreview } from "../features/advertising/CreativePreview";
import { useToastStore } from "../store/toast.store";

export function AdvertisingPage({ review = false }: { review?: boolean }) {
  const { q, set, page } = useFilters();
  const canManage = useCan("advertising.manage");
  const r = useResource(
    () =>
      api.listAds({
        page,
        page_size: 20,
        search: q.get("search") ?? "",
        agent_id: q.get("agent_id") ?? "",
        status: review ? "pending_review" : (q.get("status") ?? ""),
      }),
    [q.toString(), review],
  );
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          {review
            ? "逐版本审核素材、落地页和中国区排期。批准后新版生效，待审期间原已批准版本继续按原排期投放。"
            : "统一管理平台与商户广告，审核通过后在福利专区展示。"}
        </p>
        {!review && canManage && (
          <Link className={primary} to="/advertising/new">
            ＋ 创建广告
          </Link>
        )}
      </div>
      <Notice error={r.error} />
      <Panel>
        <div className="flex flex-wrap gap-3">
          <input
            aria-label="搜索广告"
            className={input}
            placeholder="搜索广告名称"
            value={q.get("search") ?? ""}
            onChange={(e) => set("search", e.target.value)}
          />
          <input
            aria-label="商户 ID"
            className={input}
            type="number"
            min={1}
            placeholder="商户 ID（空为全部）"
            value={q.get("agent_id") ?? ""}
            onChange={(e) => set("agent_id", e.target.value)}
          />
          {!review && (
            <select
              aria-label="审核状态"
              className={input}
              value={q.get("status") ?? ""}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="">全部审核状态</option>
              {Object.entries(STATUS).map(([key, label]) => (
                <option value={key} key={key}>
                  {label}
                </option>
              ))}
            </select>
          )}
          <button className={button} onClick={r.reload}>
            刷新
          </button>
        </div>
        <div className="overflow-auto">
          <table className="w-full min-w-[880px] text-sm text-left">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                {[
                  "广告 / 归属",
                  "广告位",
                  "当前版本 / 审核",
                  "投放状态",
                  "排期（中国时间）",
                  "操作",
                ].map((x) => (
                  <th key={x} className="p-3 font-medium">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.data?.items.map((ad) => (
                <tr key={ad.id} className="border-b border-gray-100">
                  <td className="p-3 font-medium">
                    {ad.payload.title}
                    <span className="block text-xs text-gray-400 mt-1">
                      #{ad.id} ·{" "}
                      {merchantName(ad, "平台广告")}
                    </span>
                  </td>
                  <td className="p-3">
                    {ad.payload.placements.map((p) => (
                      <span key={p.placement} className="block text-xs mb-1">
                        {PLACEMENTS[p.placement]}
                      </span>
                    ))}
                  </td>
                  <td className="p-3">
                    <span className="rounded-full bg-sky-50 text-sky-700 px-2 py-1 text-xs">
                      {STATUS[ad.status]}
                    </span>
                    <span className="block text-xs text-gray-400 mt-2">
                      修订 #{ad.current_revision_id} · 操作版本 {ad.version}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{deliveryState(ad)}</td>
                  <td className="p-3 text-xs text-gray-500">
                    {chinaDisplay(ad.payload.start_at)}
                    <br />至 {chinaDisplay(ad.payload.end_at)}
                  </td>
                  <td className="p-3">
                    <Link className="text-primary" to={`/advertising/${ad.id}`}>
                      {review ? "预览 / 审核" : "查看 / 管理"}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.loading ? (
            <p className="text-center py-10 text-sm text-gray-400">加载中…</p>
          ) : (
            !r.data?.items.length && (
              <p className="text-center py-10 text-sm text-gray-400">
                暂无符合条件的广告
              </p>
            )
          )}
        </div>
        <Pager
          total={r.data?.total ?? 0}
          page={page}
          change={(v) => set("page", String(v))}
        />
      </Panel>
    </div>
  );
}

export function AdvertisingDetailPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const manage = useCan("advertising.manage");
  const canReview = useCan("advertising.review");
  const r = useResource(
    () => (id ? api.getAd(Number(id)) : Promise.resolve(undefined)),
    [id],
  );
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(false);
  const toast = useToastStore((s) => s.addToast);
  const ad = r.data?.ad;
  async function run(fn: () => Promise<unknown>, success: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await fn();
      toast("success", success);
      setEditing(false);
      setConfirmArchive(false);
      r.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function save(payload: Payload, note: string, publish: boolean) {
    if (busy) return;
    setBusy(true);
    setError("");
    let saved: Ad | undefined;
    try {
      saved = ad
        ? await api.updateAd(ad, payload, note)
        : await api.createAd(payload);
      if (publish) {
        saved = await api.act(saved, "submit");
        saved = await api.review(saved, "approved", note || "总后台创建并发布");
      }
      toast("success", publish ? "广告已批准，将按排期投放" : "草稿已保存");
      setEditing(false);
      if (isNew) navigate(`/advertising/${saved.id}`, { replace: true });
      else r.reload();
    } catch (e) {
      setError(errorText(e));
      if (saved) {
        toast("error", "草稿已保存，发布未完成，请在详情继续操作");
        setEditing(false);
        if (isNew) navigate(`/advertising/${saved.id}`, { replace: true });
        else r.reload();
      }
    } finally {
      setBusy(false);
    }
  }
  const action = (a: Action, message: string) =>
    ad && void run(() => api.act(ad, a), message);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Link className="text-sm text-primary" to="/advertising">
          ← 返回广告管理
        </Link>
        {!isNew && (
          <button
            className={button}
            disabled={busy || editing}
            onClick={r.reload}
          >
            刷新详情
          </button>
        )}
      </div>
      <Notice error={error || r.error} />
      {isNew ? (
        <Panel title="创建中国区广告">
          {manage && (
            <AdEditor
              busy={busy}
              canPublish={canReview}
              onSave={save}
              onCancel={() => navigate("/advertising")}
            />
          )}
        </Panel>
      ) : r.loading ? (
        <p className="text-sm text-gray-400">加载中…</p>
      ) : (
        ad && (
          <>
            <Panel>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold">{ad.payload.title}</h2>
                  <p className="text-xs text-gray-500 mt-2">
                    广告 #{ad.id} ·{" "}
                    {merchantName(ad, "平台广告")} ·
                    当前修订 #{ad.current_revision_id}
                  </p>
                </div>
                <div className="text-right text-sm">
                  <span className="rounded-full bg-sky-50 text-sky-700 px-3 py-1">
                    {STATUS[ad.status]}
                  </span>
                  <p className="mt-3 text-gray-500">{deliveryState(ad)}</p>
                </div>
              </div>
              <div className="rounded-md bg-gray-50 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm">渠道编号：</span>
                  <code className="text-sm break-all">{ad.channel_code || "暂无渠道编号，请刷新获取"}</code>
                  <button
                    className={button}
                    disabled={!ad.channel_code}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(ad.channel_code);
                        toast("success", "渠道编号已复制");
                      } catch {
                        toast("error", "复制失败，请手动选择渠道编号复制");
                      }
                    }}
                  >
                    复制渠道编号
                  </button>
                </div>
                <p className="text-xs text-gray-500">同一广告在不同用户、广告位和修订版本中共用此固定渠道编号。渠道编号不可编辑。</p>
              </div>
              {ad.approved_revision_id > 0 &&
                ad.approved_revision_id !== ad.current_revision_id && (
                  <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                    当前内容尚未替换线上版本。已批准修订 #
                    {ad.approved_revision_id} 仍按原排期和开关投放。
                  </p>
                )}
              {ad.master_paused && (
                <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  总后台已暂停投放，商户无法自行恢复。
                </p>
              )}
              {!ad.archived && manage && (
                <div className="flex flex-wrap gap-2">
                  {ad.status !== "pending_review" && (
                    <button
                      className={button}
                      disabled={busy}
                      onClick={() => setEditing(!editing)}
                    >
                      {editing ? "收起编辑" : "编辑新版本"}
                    </button>
                  )}
                  {ad.status === "draft" || ad.status === "rejected" ? (
                    <button
                      className={primary}
                      disabled={busy}
                      onClick={() => action("submit", "已提交审核")}
                    >
                      提交审核
                    </button>
                  ) : (
                    ad.status === "pending_review" && (
                      <button
                        className={button}
                        disabled={busy}
                        onClick={() => action("withdraw", "已撤回待审版本")}
                      >
                        撤回待审
                      </button>
                    )
                  )}
                  <button
                    className={button}
                    disabled={busy}
                    onClick={() =>
                      action(
                        ad.paused ? "resume" : "pause",
                        ad.paused ? "已开启广告投放" : "已暂停广告投放",
                      )
                    }
                  >
                    {ad.paused ? "开启投放" : "暂停投放"}
                  </button>
                  <button
                    className={button}
                    disabled={busy}
                    onClick={() =>
                      action(
                        ad.master_paused ? "master_resume" : "master_pause",
                        ad.master_paused
                          ? "已解除总后台暂停"
                          : "已设为总后台暂停",
                      )
                    }
                  >
                    {ad.master_paused ? "解除总后台暂停" : "总后台强制暂停"}
                  </button>
                  <button
                    className={`${button} text-red-600`}
                    disabled={busy}
                    onClick={() => setConfirmArchive(true)}
                  >
                    归档广告
                  </button>
                </div>
              )}
              {confirmArchive && (
                <div
                  role="alert"
                  className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm space-y-3"
                >
                  <p>
                    归档后停止展示，无法在此恢复；历史素材、审核与统计记录仍保留。
                  </p>
                  <div className="flex gap-2">
                    <button
                      className={button}
                      disabled={busy}
                      onClick={() =>
                        action("archive", "已归档，历史记录仍保留")
                      }
                    >
                      确认归档
                    </button>
                    <button
                      className={button}
                      disabled={busy}
                      onClick={() => setConfirmArchive(false)}
                    >
                      取消
                    </button>
                  </div>
                </div>
              )}
            </Panel>
            {editing &&
            manage &&
            !ad.archived &&
            ad.status !== "pending_review" ? (
              <Panel title="编辑新版本">
                <p className="text-xs text-gray-500">
                  保存后生成新修订，已批准版本不会被直接覆盖。
                </p>
                <AdEditor
                  initial={ad.payload}
                  busy={busy}
                  canPublish={canReview && ad.agent_id === 0}
                  onSave={save}
                  onCancel={() => setEditing(false)}
                />
              </Panel>
            ) : (
              <Panel title="当前版本预览">
                <CreativePreview payload={ad.payload} />
              </Panel>
            )}
            {ad.status === "pending_review" && !ad.archived && canReview && (
              <Panel title={`审核当前修订 #${ad.current_revision_id}`}>
                <p className="text-sm text-gray-500">
                  确认以上素材、落地页、投放排期及渠道标记开关与参数名后作出审核决定。请对照已批准线上修订，避免遗漏渠道配置变更。
                </p>
                <div className="grid gap-3 md:grid-cols-2 text-sm">
                  <div className="rounded-md bg-gray-50 p-3 space-y-1">
                    <p className="font-medium">当前待审修订 #{ad.current_revision_id}</p>
                    <p>渠道标记：{ad.payload.attribution_enabled ? "开启" : "关闭"}</p>
                    <p className="break-all">渠道参数名：{ad.payload.click_id_param ?? "channel_id"}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 p-3 space-y-1">
                    <p className="font-medium">已批准线上修订 {ad.approved_payload ? `#${ad.approved_revision_id}` : "暂无"}</p>
                    {ad.approved_payload && <>
                      <p>渠道标记：{ad.approved_payload.attribution_enabled ? "开启" : "关闭"}</p>
                      <p className="break-all">渠道参数名：{ad.approved_payload.click_id_param ?? "channel_id"}</p>
                    </>}
                  </div>
                </div>
                <textarea
                  aria-label="审核意见"
                  className="w-full border border-gray-300 rounded-md p-3 text-sm"
                  rows={3}
                  placeholder="审核意见（驳回时必填）"
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                />
                <div className="flex gap-3">
                  <button
                    className={primary}
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => api.review(ad, "approved", reviewNote),
                        "审核通过，新版按排期投放",
                      )
                    }
                  >
                    批准发布
                  </button>
                  <button
                    className={`${button} text-red-600`}
                    disabled={busy || !reviewNote.trim()}
                    onClick={() =>
                      void run(
                        () => api.review(ad, "rejected", reviewNote),
                        "已驳回当前版本",
                      )
                    }
                  >
                    驳回并说明原因
                  </button>
                </div>
              </Panel>
            )}
            {ad.approved_payload &&
              ad.approved_revision_id !== ad.current_revision_id && (
                <Panel title={`已批准线上修订 #${ad.approved_revision_id}`}>
                  <CreativePreview payload={ad.approved_payload} />
                </Panel>
              )}
            <Panel title="版本历史">
              {r.data?.revisions.map((v) => (
                <details key={v.id} className="border-b border-gray-100 py-3">
                  <summary className="text-sm cursor-pointer">
                    修订 {v.revision_no} · #{v.id} ·{" "}
                    {chinaDisplay(v.created_at)} ·{" "}
                    {v.created_by_kind === "admin" ? "总后台" : "商户"}
                    {v.id === ad.approved_revision_id
                      ? " · 已批准投放版本"
                      : ""}
                  </summary>
                  <div className="pt-4">
                    <p className="text-sm text-gray-500 mb-3">
                      {v.change_note || "无修改说明"}
                    </p>
                    <CreativePreview payload={v.payload} />
                  </div>
                </details>
              ))}
            </Panel>
            <Panel title="审核记录">
              {r.data?.reviews.length ? (
                r.data.reviews.map((v) => (
                  <div
                    key={v.id}
                    className="border-b border-gray-100 pb-3 text-sm"
                  >
                    <p className="font-medium">
                      {v.decision === "approved" ? "批准" : "驳回"} · 修订 #
                      {v.revision_id}
                      <span className="font-normal text-xs text-gray-400 ml-3">
                        {chinaDisplay(v.created_at)}
                      </span>
                    </p>
                    <p className="text-gray-500 mt-2 whitespace-pre-wrap">
                      {v.note || "无备注"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">暂无审核记录</p>
              )}
            </Panel>
          </>
        )
      )}
    </div>
  );
}

function SlotEditor({
  initial,
  onSaved,
}: {
  initial: Slot;
  onSaved: () => void;
}) {
  const [slot, setSlot] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const manage = useCan("advertising.manage");
  return (
    <Panel title={PLACEMENTS[slot.placement]}>
      <p className="text-xs text-gray-500">
        {slot.placement === "home_carousel"
          ? "福利专区推荐图片"
          : "福利专区横幅图片"}{" "}
        · 中国区
      </p>
      <Notice error={error} />
      <label className="flex gap-2 text-sm">
        <input
          type="checkbox"
          checked={slot.enabled}
          disabled={!manage || busy}
          onChange={(e) => setSlot({ ...slot, enabled: e.target.checked })}
        />
        启用此广告位
      </label>
      <p className="text-xs text-gray-500">
        多条有效广告按排序在福利专区竖排展示。同一广告不重复展示。关闭此类型不会删除广告或历史统计。
      </p>
      {manage && (
        <button
          className={primary}
          disabled={
            busy ||
            !Number.isInteger(slot.interval_seconds) ||
            slot.interval_seconds < 2 ||
            slot.interval_seconds > 60
          }
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await api.updateSlot(slot);
              onSaved();
            } catch (e) {
              setError(errorText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "保存中…" : "保存配置"}
        </button>
      )}
    </Panel>
  );
}
export function AdvertisingSlotsPage() {
  const r = useResource(api.slots, []);
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        广告位独立开关与轮播节奏。现有首页运营 Banner 保持独立配置。
      </p>
      <Notice error={r.error} />
      {r.loading ? (
        <p className="text-sm text-gray-400">加载中…</p>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          {r.data?.map((s) => (
            <SlotEditor
              key={`${s.placement}-${s.version}`}
              initial={s}
              onSaved={r.reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
